import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { Radar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import 'react-datepicker/dist/react-datepicker.css';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, RadialLinearScale, Title, Tooltip, Legend, Filler, LineElement } from 'chart.js';
import './css/EnhancedAdminAITrendingDashboard.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, RadialLinearScale, Title, Tooltip, Legend, Filler, ChartDataLabels, LineElement);

const behaviorMapping = {
  "Continual Learning": { skills: ["Seeks New Skills", "Addresses Skill Gaps", "Collaborates to Learn"] },
  "Awareness": { skills: ["Recognizes Personal Limitations", "Considers Other Views", "Understands Team Role"] },
  "Magic Dust": { skills: ["Leverages Strengths", "Manages Overuse", "Recognizes Others' Strengths"] },
  "VMGM=B": { skills: ["Sets Vision", "Challenges Team", "Aligns Behavior"] },
  "Communications": { skills: ["Is Approachable", "Gives Recognition", "Listens Effectively"] },
  "Clarity": { skills: ["Presents Clear Plans", "Defines Actions", "Assigns Roles"] },
  "Accountability": { skills: ["Holds Self Accountable", "Holds Others Accountable", "Promotes Responsibility"] },
  "Conflict": { skills: ["Shows Empathy", "Addresses Disagreements", "Promotes Improvement"] },
  "Influence": { skills: ["Influences Respectfully", "Presents Confidently", "Calms Situations"] },
  "Relationships": { skills: ["Networks Effectively", "Is Authentic", "Fosters Respect"] },
  "Feedback": { skills: ["Seeks Input", "Encourages Feedback", "Uses Feedback"] },
  "Inspiration": { skills: ["Inspires Others", "Presents Ideas Enthusiastically", "Makes Others Feel Valued"] }
};

const AnalysisCard = ({ data, radarChartData, radarChartOptions, adjustScore }) => {
  if (!data) return null;

  return (
    <div className="domain-section">
      <h4>
        {data.title}
        {(data.startDate && data.endDate) && (
          <small className="criteria-inline">
            {` — ${new Date(data.startDate).toLocaleDateString()} to ${new Date(data.endDate).toLocaleDateString()}`}
          </small>
        )}
      </h4>
      {data.message ? (
        <p>{data.message}</p>
      ) : (
        <div className="results-grid">
          <div className="dashboard-card card-strengths">
            <h5 className="card-title">✨ Strengths & Opportunities</h5>
            <h6>Top Strengths</h6>
            <ul>
              {data.strengths?.map(s => (
                <li key={s.behavior} className="strength-item">{s.behavior} ({adjustScore(s.score).toFixed(2)})</li>
              )) || <li>None</li>}
            </ul>
            <h6>Top Areas for Improvement</h6>
            <ul>
              {data.areasForImprovement?.map(a => (
                <li key={a.behavior} className="opportunity-item">{a.behavior} ({adjustScore(a.score).toFixed(2)})</li>
              )) || <li>None</li>}
            </ul>
          </div>

          <div className="dashboard-card card-radar">
            <h5 className="card-title">📊 Behavior Distribution</h5>
            <div className="radar-chart-container">
              <Radar data={radarChartData} options={radarChartOptions} />
            </div>
          </div>

          <div className="dashboard-card card-gaps">
            <h5 className="card-title">🎯 Perception Gaps: Self vs. Rater</h5>
            <h6>Potential Blind Spots</h6>
            <p className="card-subtitle">Where leaders rated themselves higher than their raters.</p>
            {data.mostOverRated && data.mostOverRated.length > 0 ? (
              <ul className="hierarchical-list">
                {data.mostOverRated.map(({ behavior, totalInstances, skills }) => (
                  <li key={behavior} className="behavior-item">
                    <div className="behavior-header"><strong>{behavior}</strong> ({totalInstances} total)</div>
                    <ul className="skill-list">
                      {skills.sort((a, b) => b.instances - a.instances).map(({ skillName, instances }) => (
                        <li key={skillName} className="skill-item">{skillName} ({instances})</li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            ) : (<ul><li>None</li></ul>)}

            <h6>Potential Hidden Strengths</h6>
            <p className="card-subtitle">Where leaders rated themselves lower than their raters.</p>
            {data.mostUnderRated && data.mostUnderRated.length > 0 ? (
              <ul className="hierarchical-list">
                {data.mostUnderRated.map(({ behavior, totalInstances, skills }) => (
                  <li key={behavior} className="behavior-item">
                    <div className="behavior-header"><strong>{behavior}</strong> ({totalInstances} total)</div>
                    <ul className="skill-list">
                      {skills.sort((a, b) => b.instances - a.instances).map(({ skillName, instances }) => (
                        <li key={skillName} className="skill-item">{skillName} ({instances})</li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            ) : (<ul><li>None</li></ul>)}
          </div>

          <div className="dashboard-card card-qualitative">
            <h5 className="card-title">💬 Qualitative Feedback</h5>
            <h6>Sample Comments</h6>
            <ul>
              {data.comments?.slice(0, 3).map((comment, idx) => (
                <li key={idx} className="comment-item">"{comment}"</li>
              )) || <li>None</li>}
            </ul>
            <h6>Collection Period</h6>
            <p>{data.collectionPeriod || 'N/A'}</p>
          </div>
        </div>
      )}
    </div>
  );
};


const EnhancedAdminAITrendingDashboard = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [trendingData, setTrendingData] = useState([]);
  const [question, setQuestion] = useState('');
  const [conversation, setConversation] = useState([]);
  const [error, setError] = useState('');
  const [analysisMode, setAnalysisMode] = useState('compare');

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await axios.get('/api/organizations');
        setCompanies(response.data);
      } catch (err) {
        setError('Failed to fetch organizations.');
      }
    };
    fetchOrganizations();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setError('');
      try {
        let response;
        if (selectedCompanies.length > 0) {
          const domains = selectedCompanies.map(c => c.value);
          response = await axios.post('/api/products-by-domains', { domains });
        } else {
          response = await axios.get('/api/products');
        }
        setProducts(response.data.map(p => ({ label: p.productName, value: p._id })));
      } catch (err) {
        setError('Failed to fetch products.');
        setProducts([]);
      }
    };
    fetchProducts();
  }, [selectedCompanies]);

  const fetchComparisonData = async () => {
    setError('');
    setTrendingData([]);
    const domains = selectedCompanies.map(c => c.value);
    const productIds = selectedProducts.map(p => p.value);
    const dateRange = startDate && endDate ? {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    } : null;
    try {
      const response = await axios.post('/api/trending-report/compare', { domains, productIds, dateRange });
      setTrendingData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch comparison data.');
      setTrendingData([]);
    }
  };

  const askAi = async () => {
    if (!question.trim()) {
      setError('Please enter a question.');
      return;
    }
    setError('');
    try {
      const response = await axios.post('/api/ask-ai-trending', {
        type: 'trending',
        data: trendingData,
        question
      }, { withCredentials: true });
      setConversation([...conversation, { question, answer: response.data.response }]);
      setQuestion('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to get AI response.');
    }
  };

  const adjustScore = (score) => Math.max(0, parseFloat(score) - 1);

  const combinedData = useMemo(() => {
    if (analysisMode !== 'combine' || trendingData.length === 0) {
      return null;
    }

    const validData = trendingData.filter(d => !d.message);
    if (validData.length === 0) return { title: 'Combined Analysis', message: 'No data available to combine.' };

    const combinedBehaviorScores = {};
    Object.keys(behaviorMapping).forEach(behavior => {
      const scores = validData
        .map(d => d.allBehaviors?.find(b => b.behavior === behavior)?.score)
        .filter(score => score != null)
        .map(score => parseFloat(score));
      if (scores.length > 0) {
        combinedBehaviorScores[behavior] = scores.reduce((a, b) => a + b, 0) / scores.length;
      }
    });

    const allBehaviors = Object.entries(combinedBehaviorScores).map(([behavior, score]) => ({ behavior, score }));

    const sortedBehaviors = [...allBehaviors].sort((a, b) => b.score - a.score);
    const strengths = sortedBehaviors.slice(0, 3);
    const areasForImprovement = sortedBehaviors.slice(-3).reverse();

    const mostOverRated = {};
    const mostUnderRated = {};
    validData.forEach(d => {
      d.mostOverRated?.forEach(gap => {
        if (!mostOverRated[gap.behavior]) mostOverRated[gap.behavior] = { behavior: gap.behavior, totalInstances: 0, skills: {} };
        mostOverRated[gap.behavior].totalInstances += gap.totalInstances;
        gap.skills.forEach(skill => {
          mostOverRated[gap.behavior].skills[skill.skillName] = (mostOverRated[gap.behavior].skills[skill.skillName] || 0) + skill.instances;
        });
      });
      d.mostUnderRated?.forEach(gap => {
        if (!mostUnderRated[gap.behavior]) mostUnderRated[gap.behavior] = { behavior: gap.behavior, totalInstances: 0, skills: {} };
        mostUnderRated[gap.behavior].totalInstances += gap.totalInstances;
        gap.skills.forEach(skill => {
          mostUnderRated[gap.behavior].skills[skill.skillName] = (mostUnderRated[gap.behavior].skills[skill.skillName] || 0) + skill.instances;
        });
      });
    });

    const formatGaps = (gaps) => Object.values(gaps)
        .map(b => ({ ...b, skills: Object.entries(b.skills).map(([skillName, instances]) => ({ skillName, instances }))}))
        .sort((a,b) => b.totalInstances - a.totalInstances)
        .slice(0,3);

    const comments = validData.flatMap(d => d.comments || []);

    return {
      title: 'Combined Analysis',
      allBehaviors,
      strengths,
      areasForImprovement,
      mostOverRated: formatGaps(mostOverRated),
      mostUnderRated: formatGaps(mostUnderRated),
      comments,
      collectionPeriod: validData[0].collectionPeriod,
      startDate: startDate,
      endDate: endDate,
    };
  }, [trendingData, analysisMode, startDate, endDate]);

  const radarChartData = useMemo(() => {
    if (analysisMode === 'combine' && combinedData && !combinedData.message) {
      return {
        labels: Object.keys(behaviorMapping),
        datasets: [{
          label: 'Combined Average',
          data: Object.keys(behaviorMapping).map(label => {
            const behavior = combinedData.allBehaviors?.find(b => b.behavior === label);
            return behavior ? adjustScore(behavior.score) : 0;
          }),
          backgroundColor: 'rgba(255, 99, 132, 0.4)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 2,
        }]
      };
    }
    return trendingData.map(data => ({
      labels: Object.keys(behaviorMapping),
      datasets: [{
        label: data.domain || data.productName || 'Analysis',
        data: Object.keys(behaviorMapping).map(label => {
          const behavior = data.allBehaviors?.find(b => b.behavior === label);
          return behavior ? adjustScore(behavior.score) : 0;
        }),
        backgroundColor: 'rgba(75, 192, 192, 0.4)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 2,
      }]
    }));
  }, [analysisMode, trendingData, combinedData]);

  const radarChartOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      title: { display: true, text: 'Behavior Distribution' },
      tooltip: {
        callbacks: {
          label: context => `${context.label}: ${context.parsed.r.toFixed(2)}`
        }
      },
      datalabels: {
        formatter: value => value.toFixed(2),
        color: '#000',
        font: { weight: 'bold' }
      }
    },
    scales: {
      r: { beginAtZero: true, max: 5, ticks: { stepSize: 1 } }
    }
  };

  const renderAIResponse = (response) => {
    try {
      const parsed = JSON.parse(response);
      return <pre>{JSON.stringify(parsed, null, 2)}</pre>;
    } catch {
      return <p>{response}</p>;
    }
  };

  return (
    <div className="dashboard-container">
      <h2>Enhanced AI Trending Dashboard</h2>
      <div className="controls">
        <Select
          isMulti
          options={companies}
          value={selectedCompanies}
          onChange={setSelectedCompanies}
          placeholder="Select companies to analyze..."
          className="company-select"
        />
        <Select
          isMulti
          options={products}
          value={selectedProducts}
          onChange={setSelectedProducts}
          placeholder="Select products to analyze..."
          className="product-select"
        />
        <div className="date-pickers">
          <DatePicker
            selected={startDate}
            onChange={date => setStartDate(date)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            placeholderText="Start Date"
          />
          <DatePicker
            selected={endDate}
            onChange={date => setEndDate(date)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            placeholderText="End Date"
          />
        </div>
        
        <div className="mode-toggle-container">
            <span>Compare</span>
            <label className="switch">
                <input 
                    type="checkbox" 
                    checked={analysisMode === 'combine'} 
                    onChange={(e) => setAnalysisMode(e.target.checked ? 'combine' : 'compare')}
                />
                <span className="slider round"></span>
            </label>
            <span>Combine</span>
        </div>

        <button
          onClick={fetchComparisonData}
          disabled={selectedCompanies.length === 0 && selectedProducts.length === 0}
          className="compare-button"
        >
          Analyze
        </button>
      </div>
      {error && <p className="error">{error}</p>}

      {trendingData.length > 0 && (
        <div className="results">
          <h3>
            {analysisMode === 'combine' ? 'Combined Trend Analysis' : 'Comparison Results'}
            {(selectedCompanies.length || selectedProducts.length || (startDate && endDate)) && (
              <span className="criteria">
                {' — '}
                {selectedCompanies.length > 0 && `Org: ${selectedCompanies.map(c => c.label).join(', ')}`}
                {selectedProducts.length > 0 && ` ${selectedCompanies.length > 0 ? '|' : ''} Products: ${selectedProducts.map(p => p.label).join(', ')}`}
                {(startDate && endDate) && ` ${selectedProducts.length || selectedCompanies.length ? '|' : ''} Date: ${startDate ? startDate.toLocaleDateString() : ''}–${endDate ? endDate.toLocaleDateString() : ''}`}
              </span>
            )}
          </h3>

          {analysisMode === 'combine' ? (
            <AnalysisCard 
              data={combinedData}
              radarChartData={radarChartData}
              radarChartOptions={radarChartOptions}
              adjustScore={adjustScore}
            />
          ) : (
            trendingData.map((data, index) => (
              <AnalysisCard 
                key={data.domain || data.productName || index}
                data={{
                  ...data,
                  title: data.domain ? `Domain: ${data.domain}` : `Product: ${data.productName}`,
                  startDate: startDate,
                  endDate: endDate,
                }}
                radarChartData={radarChartData[index]}
                radarChartOptions={radarChartOptions}
                adjustScore={adjustScore}
              />
            ))
          )}
        </div>
      )}

      <div className="ai-section">
        <h3>Ask AI</h3>
        <textarea
          value={question}
          onChange={e => setQuestion(e.target.value)}
          placeholder="e.g., How do strengths differ between products?"
        />
        <button
          onClick={askAi}
          disabled={trendingData.length === 0 || !question.trim()}
          className="ask-button"
        >
          Ask
        </button>
        <div className="conversation">
          {conversation.map((item, idx) => (
            <div key={idx} className="conversation-item">
              <p><strong>You:</strong> {item.question}</p>
              <div><strong>AI:</strong> {renderAIResponse(item.answer)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EnhancedAdminAITrendingDashboard;